import React, { useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Collapse, Typography } from '@mui/material';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { ToolbarButton } from '../inputs/ToolbarButton';
import { DownloadButton, type DownloadFormat } from '../inputs/DownloadButton';

export interface FileOfferAction {
  key: string;
  /** The format's short name: "Excel", "CSV". */
  label: string;
  onDownload: () => void;
  /** This file is being prepared. */
  loading?: boolean;
}

export interface FileOfferAlternative {
  key: string;
  title: string;
  meta?: string;
  formats: DownloadFormat[];
}

export interface FileOfferCardProps {
  title: string;
  /** "12,431 rows · 8 columns". */
  meta?: string;
  icon?: React.ReactNode;
  /** The format that was asked for — the card's one main button. */
  primary: FileOfferAction;
  /** The other formats, quieter, one click away. */
  others?: FileOfferAction[];
  /** No download can start right now, e.g. another is being prepared. */
  disabled?: boolean;
  /** The file can no longer be made; says why, in place of the buttons. */
  unavailable?: string;
  /** Other files it might have meant, behind "Not this one?". */
  alternatives?: FileOfferAlternative[];
}

/**
 * A file that is ready to download — what "send me that as Excel" leaves in
 * a conversation. Nothing downloads until someone presses the button.
 *
 * Shaped like a result card: the icon tile, the name and a line of facts on
 * the left; on the right one main button for the format that was asked for,
 * the others quieter beside it. If the wrong file was picked, "Not this one?"
 * opens the others, each with the usual Download control.
 */
export function FileOfferCard({
  title,
  meta,
  icon,
  primary,
  others = [],
  disabled,
  unavailable,
  alternatives = [],
}: FileOfferCardProps) {
  const [open, setOpen] = useState(false);
  const gone = Boolean(unavailable);

  return (
    <Box
      sx={(t) => ({
        flexShrink: 0,
        border: `1px solid ${t.tokens.border.default}`,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.25, rowGap: 1, px: 1.5, py: 1.25 }}>
        <Box
          aria-hidden
          sx={(t) => ({
            width: t.ds.sizes.control.md,
            height: t.ds.sizes.control.md,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: gone ? t.tokens.semantic.dangerSoft : t.tokens.brand.primarySoft,
            color: gone ? t.tokens.semantic.danger : t.tokens.text.accent,
          })}
        >
          {gone ? <ErrorOutlineRounded fontSize="small" /> : icon ?? <TableChartOutlined fontSize="small" />}
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap title={title}>
            {title}
          </Typography>
          {(unavailable || meta) && (
            <Typography variant="caption" component="div" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {unavailable ?? meta}
            </Typography>
          )}
        </Box>
        {!gone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto', flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={disabled}
              onClick={primary.onDownload}
              startIcon={primary.loading ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlined fontSize="small" />}
              sx={(t) => ({ minHeight: t.ds.sizes.control.sm, whiteSpace: 'nowrap' })}
            >
              {primary.loading ? 'Preparing' : `Download ${primary.label}`}
            </Button>
            {others.map((o) => (
              <ToolbarButton
                key={o.key}
                variant="ghost"
                onClick={o.onDownload}
                loading={o.loading}
                loadingLabel="Preparing"
                disabled={disabled}
                tooltip={`Download as ${o.label} instead`}
              >
                {o.label}
              </ToolbarButton>
            ))}
          </Box>
        )}
      </Box>

      {!gone && alternatives.length > 0 && (
        <>
          <ButtonBase
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            sx={(t) => ({
              ...t.typography.caption,
              width: '100%',
              justifyContent: 'space-between',
              px: 1.5,
              py: 0.75,
              fontWeight: 500,
              color: t.tokens.text.secondary,
              borderTop: `1px solid ${t.tokens.border.subtle}`,
              '&:hover': { bgcolor: t.tokens.surface.sunken, color: t.tokens.text.primary },
              '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
              '& svg': {
                transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
                transform: open ? 'rotate(180deg)' : 'none',
              },
            })}
          >
            Not this one?
            <ExpandMoreRounded fontSize="small" />
          </ButtonBase>
          <Collapse in={open} unmountOnExit>
            <Box
              component="ul"
              aria-label="Other files"
              sx={(t) => ({ listStyle: 'none', m: 0, px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1, bgcolor: t.tokens.surface.sunken })}
            >
              {alternatives.map((alt) => (
                <Box component="li" key={alt.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={alt.title}>
                      {alt.title}
                    </Typography>
                    {alt.meta && (
                      <Typography variant="caption" component="div" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                        {alt.meta}
                      </Typography>
                    )}
                  </Box>
                  <DownloadButton formats={alt.formats} disabled={disabled} />
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}
